import React, { FC, useState } from 'react'

import { useQuery } from '@apollo/client'
import { RenderFunctionResult } from 'react-on-rails/node_package/lib/types'

import {
  AppContext,
  GET_CURRENT_ACCOUNT,
  GET_CURRENT_CART,
  GET_CURRENT_LOCATION,
  GET_CURRENT_SHOP,
  GET_CURRENT_CUSTOMER,
  railsContext,
} from 'Const'
import useEvent from 'Hooks/useEvent'
import { Account, Cart, CheckoutPayload, Location, Shop, Customer } from 'Types'
import { sendFbPurchaseEvent } from 'Utils/analytics/fbEvents'
import { sendCustomPurchaseEvent, sendGtagUAPurchaseEvent, sendGTMOrderSubmittedEvent } from 'Utils/analytics/gtmEvents'
import { sendKlaviyoPurchaseEvent } from 'Utils/analytics/klaviyoEvents'
import { isDropOff } from 'Utils/cartFulfillment'

import GQLProvider from 'Components/GQLProvider'
import SpinnerWheel from 'Components/SpinnerWheel'

import Form from './Form'
import OrderDetails from './OrderDetails'

interface Props {
  account: Account
  authenticityToken: string
  cart: Cart
  currentLocation: Location
  customer: Customer
  googlePlacesApiKey: string
  locationCountryIsoCodes: string // as json
  shop: Shop
}

const Checkout: FC<Props> = ({
  account,
  cart,
  shop,
  customer,
  currentLocation: location,
  locationCountryIsoCodes: location_country_iso_codes,
  ...rest
}) => {
  const { data: currentCustomerData, loading } = useQuery(GET_CURRENT_CUSTOMER)
  const { data: currentCartData } = useQuery(GET_CURRENT_CART)
  const { data: currentAccountData } = useQuery(GET_CURRENT_ACCOUNT)
  const { data: currentShopData } = useQuery(GET_CURRENT_SHOP)
  const { data: currentLocationData } = useQuery(GET_CURRENT_LOCATION)

  const locationCountryIsoCodes = JSON.parse(location_country_iso_codes)
  const currentCustomer = (currentCustomerData?.currentCustomer || customer) as Customer
  const currentCart = (currentCartData?.currentCart || cart) as Cart
  const currentAccount = (currentAccountData?.currentAccount || account) as Account
  const currentShop = (currentShopData?.currentShop || shop) as Shop
  const currentLocation = (currentLocationData?.currentLocation || location) as Location

  const [checkout, setCheckout] = useState<CheckoutPayload>()

  const handleCheckout = useEvent((checkout: CheckoutPayload | undefined) => {
    setCheckout(checkout)

    if (!checkout || (checkout.payment && checkout.payment.status !== 'succeeded')) return

    const orderUuid = checkout.order.uuid
    const fulfillment = cart?.fulfillment

    // Send events
    sendGTMOrderSubmittedEvent(cart, currentLocation, shop)
    sendCustomPurchaseEvent(cart)
    sendGtagUAPurchaseEvent(cart, currentLocation)
    sendFbPurchaseEvent(cart)
    sendKlaviyoPurchaseEvent(cart, currentLocation, shop)

    if (!railsContext.serverSide) {
      let redirectPath = `/shop/order/${orderUuid}`
      if (isDropOff(fulfillment)) {
        redirectPath = `/d/${fulfillment.dropOffChannel.handle}`
      }
      window.location.replace(redirectPath)
    }
  })

  return !currentCustomer && loading ? (
    <SpinnerWheel additionalClassNames="spinner-wheel-black spinner-wheel-50" wrapperClassName="text-center mt-10" />
  ) : (
    <AppContext.Provider
      value={{
        cart: currentCart,
        account: currentAccount,
        shop: currentShop,
        locationCountryIsoCodes,
        currentLocation: currentLocation,
        customer: currentCustomer,
        checkout,
        onCheckout: handleCheckout,
        ...rest,
      }}
    >
      <section className="py-10">
        <div className="container">
          {currentCart.noItems ? (
            <h3 className="text-center / py-8">Your cart is empty</h3>
          ) : (
            <div className="row">
              <div className="order-2 order-lg-1 / col-lg-7">
                <Form />
              </div>
              <OrderDetails />
            </div>
          )}
        </div>
      </section>
    </AppContext.Provider>
  )
}

const Wrapper = (props: Props): RenderFunctionResult => {
  const Wrapped = () => (
    <GQLProvider>
      <Checkout {...props} />
    </GQLProvider>
  )

  return Wrapped
}

Wrapper.renderFunction = true

export default Wrapper
